const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷', name: 'Français', description: 'Langue principale' },
  { code: 'en', flag: '🇬🇧', name: 'English', description: 'English interface' },
  { code: 'pt', flag: '🇵🇹', name: 'Português', description: 'Português' },
  { code: 'ar', flag: '🇸🇦', name: 'عربي', description: 'اللغة العربية' },
  { code: 'sw', flag: '🇰🇪', name: 'Kiswahili', description: 'Afrika Mashariki' },
  { code: 'wo', flag: '🇸🇳', name: 'Wolof', description: 'Sénégal / Gambie' },
];

interface LanguageModalProps {
  showLanguageModal: boolean;
  setShowLanguageModal: (show: boolean) => void;
  selectedLanguage: string;
  setSelectedLanguage: (lang: string) => void;
}

export function LanguageModal({
  showLanguageModal, setShowLanguageModal,
  selectedLanguage, setSelectedLanguage,
}: LanguageModalProps) {
  if (!showLanguageModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => setShowLanguageModal(false)}>
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-xl font-bold text-gray-900">Choisir la langue</h3>
            <button onClick={() => setShowLanguageModal(false)}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
              <i className="ri-close-line text-gray-600" />
            </button>
          </div>

          <div className="space-y-2">
            {LANGUAGES.map(lang => (
              <button key={lang.code}
                onClick={() => { setSelectedLanguage(lang.code); setShowLanguageModal(false); }}
                className={`w-full flex items-center gap-4 p-3.5 rounded-xl transition-all ${selectedLanguage === lang.code
                  ? 'bg-teal-50 border-2 border-teal-500'
                  : 'border-2 border-transparent hover:bg-gray-50'
                  }`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <div className="text-left flex-1">
                  <p className="font-semibold text-gray-900 text-sm">{lang.name}</p>
                  <p className="text-xs text-gray-500">{lang.description}</p>
                </div>
                {selectedLanguage === lang.code && (
                  <i className="ri-check-line text-teal-600 text-lg" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LanguageModal;

// ═══════════════════════════════════════════════════════════════
//  MobileMoneyModal.tsx  (dans le même fichier pour concision)
// ═══════════════════════════════════════════════════════════════

const PROVIDERS = [
  { id: 1, name: 'Orange Money', color: 'bg-orange-500', icon: 'ri-smartphone-line', countries: ['Guinée', 'Côte d\'Ivoire', 'Sénégal'] },
  { id: 2, name: 'MTN Money', color: 'bg-yellow-500', icon: 'ri-smartphone-line', countries: ['Guinée', 'Cameroun', 'Ghana'] },
  { id: 3, name: 'Moov Money', color: 'bg-blue-600', icon: 'ri-bank-card-line', countries: ['Côte d\'Ivoire', 'Burkina', 'Mali'] },
  { id: 4, name: 'Wave', color: 'bg-cyan-500', icon: 'ri-send-plane-line', countries: ['Sénégal', 'Côte d\'Ivoire'] },
  { id: 5, name: 'M-Pesa', color: 'bg-green-600', icon: 'ri-money-dollar-box-line', countries: ['Kenya', 'Tanzania', 'DRC'] },
  { id: 6, name: 'Airtel Money', color: 'bg-red-600', icon: 'ri-smartphone-line', countries: ['Uganda', 'Zambie', 'Rwanda'] },
];

interface MobileMoneyProps {
  showMobileMoney: boolean;
  setShowMobileMoney: (show: boolean) => void;
}

export function MobileMoneyModal({ showMobileMoney, setShowMobileMoney }: MobileMoneyProps) {
  if (!showMobileMoney) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => setShowMobileMoney(false)}>
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-xl font-bold text-gray-900">Solutions Mobile Money</h3>
            <button onClick={() => setShowMobileMoney(false)}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
              <i className="ri-close-line text-gray-600" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Payez vos livraisons depuis votre téléphone. Zéro commission pour les clients.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {PROVIDERS.map(p => (
              <div key={p.id}
                className="flex items-center gap-3 p-3.5 border border-gray-200 rounded-xl hover:border-teal-200 hover:bg-teal-50/30 transition-all">
                <div className={`w-10 h-10 ${p.color} rounded-lg flex items-center justify-center shrink-0`}>
                  <i className={`${p.icon} text-white`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.countries.slice(0, 2).map(c => (
                      <span key={c} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={() => setShowMobileMoney(false)}
            className="mt-6 w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-xl
              font-semibold text-sm transition-colors">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
